import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, FileText, Gauge, Copy, Loader2, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * ContentFactoryPanel — Phase 6 admin UI.
 * Two modes:
 *  - Draft: LLM-generate a new article (topic + optional city + keywords)
 *  - Scorecard: show all articles ranked by SEO score with drill-in per slug
 */
export default function ContentFactoryPanel() {
    const [mode, setMode] = useState("draft");
    return (
        <div className="space-y-6" data-testid="content-factory-panel">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => setMode("draft")}
                    data-testid="cf-mode-draft"
                    className={`px-4 py-2 rounded-lg text-[13px] border ${mode === "draft" ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#E5E7EB]"}`}
                ><Sparkles size={14} className="inline mr-1" /> Draft new article</button>
                <button
                    onClick={() => setMode("scorecard")}
                    data-testid="cf-mode-scorecard"
                    className={`px-4 py-2 rounded-lg text-[13px] border ${mode === "scorecard" ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#E5E7EB]"}`}
                ><Gauge size={14} className="inline mr-1" /> SEO scorecard</button>
            </div>
            {mode === "draft" ? <DraftPanel /> : <ScorecardPanel />}
        </div>
    );
}

function DraftPanel() {
    const [topic, setTopic] = useState("");
    const [city, setCity] = useState("");
    const [keywords, setKeywords] = useState("");
    const [medical, setMedical] = useState(true);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!topic.trim()) return;
        setLoading(true); setDraft(null);
        try {
            const kws = keywords.split(/[;,|]/).map((k) => k.trim()).filter(Boolean);
            const r = await api.post("/admin/content/draft", {
                topic: topic.trim(), city: city.trim() || null, keywords: kws, medical,
            });
            setDraft(r.data);
            toast.success(`Drafted "${r.data.title}" — SEO ${r.data.seo_score.score}/100`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Draft generation failed");
        } finally { setLoading(false); }
    };

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(draft, null, 2));
        toast.success("JSON copied to clipboard");
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-black/5 space-y-4" data-testid="cf-draft-form">
                <div>
                    <h3 className="font-serif text-[18px] mb-1">Generate a Signia Styletto IX article</h3>
                    <p className="text-[13px] text-[#4a5568]">Powered by Claude Sonnet 4.6. Draft is 1,500–2,000 words with 4–6 FAQs and SEO scorecard.</p>
                </div>
                <div>
                    <label className="text-[12px] text-[#4a5568] uppercase tracking-wide">Topic</label>
                    <input
                        required
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder='e.g. "Signia Styletto IX vs Phonak Slim in India"'
                        className="mt-1 h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]"
                        data-testid="cf-topic-input"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[12px] text-[#4a5568] uppercase tracking-wide">City (optional)</label>
                        <input
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder="e.g. Hyderabad"
                            className="mt-1 h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]"
                            data-testid="cf-city-input"
                        />
                    </div>
                    <div className="flex items-end">
                        <label className="text-[13px] flex items-center gap-2 h-11">
                            <input
                                type="checkbox"
                                checked={medical}
                                onChange={(e) => setMedical(e.target.checked)}
                                data-testid="cf-medical-checkbox"
                            />
                            Medical-reviewed (emits MedicalWebPage schema)
                        </label>
                    </div>
                </div>
                <div>
                    <label className="text-[12px] text-[#4a5568] uppercase tracking-wide">Keywords (comma or semicolon-separated)</label>
                    <input
                        value={keywords}
                        onChange={(e) => setKeywords(e.target.value)}
                        placeholder="hearing aid Hyderabad, Signia clinic Banjara Hills"
                        className="mt-1 h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]"
                        data-testid="cf-keywords-input"
                    />
                </div>
                <button
                    disabled={loading || !topic.trim()}
                    type="submit"
                    data-testid="cf-generate-btn"
                    className="w-full h-11 rounded-lg bg-[#003E4D] text-white text-[14px] flex items-center justify-center gap-2 disabled:opacity-40"
                >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Drafting… (~15–25s)</> : <><Sparkles size={16} /> Generate draft</>}
                </button>
                <p className="text-[11px] text-[#9CA3AF]">Drafts are not auto-saved. Copy the JSON into a code commit or the content management flow.</p>
            </form>

            <div className="bg-white rounded-2xl p-6 border border-black/5" data-testid="cf-draft-preview">
                {!draft ? (
                    <div className="text-[14px] text-[#4a5568] flex items-center gap-2 h-full">
                        <FileText size={16} /> Draft preview will appear here.
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h4 className="font-serif text-[18px]">{draft.title}</h4>
                            <button onClick={copyJson} className="text-[12px] px-3 py-1 rounded border border-[#E5E7EB] flex items-center gap-1" data-testid="cf-copy-json"><Copy size={12} /> Copy JSON</button>
                        </div>
                        <div className="text-[12px] text-[#4a5568]">slug: <code>{draft.slug}</code></div>
                        <div className="text-[13px] text-[#4a5568] italic">{draft.meta_description}</div>
                        <div className="flex items-center gap-2 text-[12px]">
                            <span className={`px-2 py-1 rounded text-white ${draft.seo_score.score >= 80 ? "bg-emerald-600" : draft.seo_score.score >= 60 ? "bg-amber-600" : "bg-rose-600"}`}>
                                SEO {draft.seo_score.score}/100
                            </span>
                            <span>{draft.read_minutes} min read</span>
                            <span>{(draft.sections || []).length} sections</span>
                            <span>{(draft.faqs || []).length} FAQs</span>
                        </div>
                        {draft.seo_score.findings?.length > 0 && (
                            <details className="text-[12px]">
                                <summary className="cursor-pointer text-amber-700">Findings ({draft.seo_score.findings.length})</summary>
                                <ul className="mt-2 space-y-1 pl-4 list-disc text-[#4a5568]">
                                    {draft.seo_score.findings.map((f, i) => <li key={i}>{f}</li>)}
                                </ul>
                            </details>
                        )}
                        <details>
                            <summary className="cursor-pointer text-[13px]">Sections ({(draft.sections || []).length})</summary>
                            <div className="mt-2 space-y-3 text-[13px] max-h-96 overflow-y-auto">
                                {(draft.sections || []).map((s, i) => (
                                    <div key={i} className="border-l-2 border-[#003E4D] pl-3">
                                        <div className="font-medium mb-1">{s.h2}</div>
                                        <div className="text-[#4a5568]" dangerouslySetInnerHTML={{ __html: (s.body || "").slice(0, 400) + "…" }} />
                                    </div>
                                ))}
                            </div>
                        </details>
                        <details>
                            <summary className="cursor-pointer text-[13px]">FAQs ({(draft.faqs || []).length})</summary>
                            <div className="mt-2 space-y-2 text-[13px] max-h-64 overflow-y-auto">
                                {(draft.faqs || []).map((f, i) => (
                                    <div key={i} className="border-l-2 border-[#94A3B8] pl-3">
                                        <div className="font-medium">{f.q}</div>
                                        <div className="text-[#4a5568]">{f.a}</div>
                                    </div>
                                ))}
                            </div>
                        </details>
                    </div>
                )}
            </div>
        </div>
    );
}

function ScorecardPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await api.get("/admin/content/scorecard-all");
                setRows(r.data.articles || []);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Load failed");
            } finally { setLoading(false); }
        })();
    }, []);

    const filtered = rows.filter((r) =>
        !filter ||
        r.slug.includes(filter.toLowerCase()) ||
        r.title.toLowerCase().includes(filter.toLowerCase()) ||
        (r.category || "").toLowerCase().includes(filter.toLowerCase())
    );

    const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.score, 0) / rows.length) : 0;
    const low = rows.filter((r) => r.score < 60).length;
    const high = rows.filter((r) => r.score >= 80).length;

    return (
        <div className="space-y-4" data-testid="cf-scorecard">
            <div className="grid grid-cols-4 gap-3">
                <StatCard label="Total articles" value={rows.length} />
                <StatCard label="Average score" value={`${avg}/100`} tone={avg >= 80 ? "good" : avg >= 60 ? "warn" : "bad"} />
                <StatCard label="High-quality (80+)" value={high} tone="good" />
                <StatCard label="Needs work (<60)" value={low} tone={low > 0 ? "bad" : "good"} />
            </div>
            <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by slug, title or category…"
                className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]"
                data-testid="cf-scorecard-filter"
            />
            <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-[14px] text-[#4a5568]"><Loader2 size={16} className="inline animate-spin mr-1" /> Loading scorecards…</div>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#F9FAFB] text-left">
                            <tr>
                                <th className="p-3">Article</th>
                                <th className="p-3">Category</th>
                                <th className="p-3 text-right">Words</th>
                                <th className="p-3 text-right">Sections</th>
                                <th className="p-3 text-right">FAQs</th>
                                <th className="p-3">Medical</th>
                                <th className="p-3 text-right">Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.slug} className="border-t border-[#F3F4F6]" data-testid={`cf-row-${r.slug}`}>
                                    <td className="p-3">
                                        <div className="font-medium">{r.title}</div>
                                        <div className="text-[11px] text-[#9CA3AF]">/{r.slug}</div>
                                    </td>
                                    <td className="p-3 text-[#4a5568]">{r.category || "—"}</td>
                                    <td className="p-3 text-right">{r.word_count}</td>
                                    <td className="p-3 text-right">{r.sections}</td>
                                    <td className="p-3 text-right">{r.faqs}</td>
                                    <td className="p-3">{r.medical_reviewed ? "✓" : "—"}</td>
                                    <td className="p-3 text-right">
                                        <span className={`inline-block px-2 py-0.5 rounded text-white text-[11px] ${r.score >= 80 ? "bg-emerald-600" : r.score >= 60 ? "bg-amber-600" : "bg-rose-600"}`}>
                                            {r.score}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, tone }) {
    const bg = tone === "good" ? "bg-emerald-50" : tone === "warn" ? "bg-amber-50" : tone === "bad" ? "bg-rose-50" : "bg-white";
    return (
        <div className={`p-4 rounded-xl border border-black/5 ${bg}`}>
            <div className="text-[11px] text-[#4a5568] uppercase tracking-wide">{label}</div>
            <div className="text-[22px] font-serif mt-1">{value}</div>
        </div>
    );
}
